/**
 * WorkflowDrawer — Add/Edit a workflow within a careflow.
 *
 * Fields: name, mute toggle (non-default types), DayTimeSlider (0–86400).
 *
 * Props:
 *   open           — drawer visibility
 *   onClose        — close callback
 *   onSave         — save callback (formData)
 *   onDelete       — delete callback (for edit mode)
 *   saving         — boolean, disables buttons while saving
 *   editTarget     — null for add, workflow object for edit
 *   careflowTypeId — parent careflow type (1, 3, 4, 5, 6)
 */

import { useState, useEffect, useMemo } from 'react';
import DayTimeSlider from './DayTimeSlider.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

const WorkflowDrawer = ({
    open,
    onClose,
    onSave,
    onDelete,
    saving,
    editTarget,
    careflowTypeId,
}) => {
    const isEdit = !!editTarget;
    const showMute = careflowTypeId !== 1;

    const [name, setName] = useState('');
    const [isMute, setIsMute] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(86400);
    const [errors, setErrors] = useState({});
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    // Reset form when drawer opens
    useEffect(() => {
        if (!open) return;

        if (editTarget) {
            setName(editTarget.workflow_name || '');
            setIsMute(!!editTarget.is_mute);
            setStartTime(editTarget.window_start ?? 0);
            setEndTime(editTarget.window_end ?? 86400);
        } else {
            setName('');
            setIsMute(false);
            setStartTime(0);
            setEndTime(86400);
        }
        setErrors({});
        setConfirmingDelete(false);
    }, [open, editTarget]);

    const namePattern = /^[a-zA-Z0-9_ \-]+$/;

    const validate = () => {
        const errs = {};
        if (!name.trim()) errs.name = 'Name is required.';
        else if (!namePattern.test(name.trim())) errs.name = 'Only letters, numbers, spaces, dashes, and underscores are allowed.';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // Live form validity for button state
    const isFormValid = useMemo(() => {
        const n = name.trim();
        return !!n && namePattern.test(n);
    }, [name]);

    const handleSave = () => {
        if (!validate()) return;
        onSave({
            workflow_name: name.trim(),
            is_mute: showMute ? (isMute ? 1 : 0) : 0,
            window_start: startTime,
            window_end: endTime,
        });
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title={isEdit ? 'Edit Workflow' : 'Add Workflow'}
                footerProps={{ style: { flexDirection: 'column', gap: '0.5rem' } }}
                footer={(
                    <>
                        {confirmingDelete ? (
                            <div className="cf-inline-confirm">
                                <p className="cf-inline-confirm__message">
                                    Are you sure you want to delete this workflow and all its escalations and outputs?
                                </p>
                                <div className="cf-inline-confirm__actions">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setConfirmingDelete(false)} disabled={saving}>
                                        No, Keep It
                                    </button>
                                    <button className="dp-btn dp-btn--danger" onClick={onDelete} disabled={saving}>
                                        {saving ? 'Deleting...' : 'Yes, Delete'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                {isEdit && onDelete ? (
                                    <button className="dp-btn dp-btn--danger" onClick={() => setConfirmingDelete(true)} disabled={saving}>
                                        Delete
                                    </button>
                                ) : (
                                    <span />
                                )}
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                                    <button className="dp-btn dp-btn--primary" disabled={saving || !isFormValid} onClick={handleSave}>
                                        {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Workflow')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            >
                {/* Name */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">
                        Name <span className="dp-drawer__required">*</span>
                    </label>
                    <input
                        className={`dp-drawer__input${errors.name ? ' is-invalid' : ''}`}
                        type="text"
                        value={name}
                        placeholder="Enter workflow name"
                        onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }}
                    />
                    {errors.name ? <p className="dp-drawer__error">{errors.name}</p> : null}
                </div>

                {/* Mute toggle (non-default types) */}
                {showMute ? (
                    <div className="cf-output-toggle-row">
                        <span className="cf-output-toggle-label">Workflow is Muted</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={isMute}
                                onChange={(e) => setIsMute(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>
                ) : null}

                {/* Time window slider */}
                <div style={{ marginTop: 'var(--space-4, 1rem)' }}>
                    <label className="dp-drawer__label" style={{ marginBottom: 'var(--space-2, 0.5rem)' }}>
                        Time Window
                    </label>
                    <DayTimeSlider
                        start={startTime}
                        end={endTime}
                        setStart={setStartTime}
                        setEnd={setEndTime}
                    />
                </div>
            </Drawer>
        </>
    );
};

export default WorkflowDrawer;
